// File: src/components/AppWarmup.jsx
// FIXED: Cache keys now match the actual keys used in page components
// This prevents data from reloading when switching pages

import { useEffect, useState, useContext } from 'react';
import { useDataCache } from '../context/DataCacheProvider';
import { AuthContext } from '../context/AuthContext';

export default function AppWarmup() {
  const { token } = useContext(AuthContext);
  const cache = useDataCache();

  const API = 'https://api.baobrain.com';
  const [siteId, setSiteId] = useState(null);
  const [daysRange, setDaysRange] = useState('30');
  const [propertyId, setPropertyId] = useState(null);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [siteToken, setSiteToken] = useState(null);

  // First, fetch sites to get a valid site_id
  useEffect(() => {
    if (!token) return;

    let mounted = true;

    (async () => {
      try {
        const existingSiteId = sessionStorage.getItem('site_id');
        const existingSiteToken = sessionStorage.getItem('site_token');
        const existingPlatform = sessionStorage.getItem('site_platform');

        if (existingSiteId && existingSiteToken && existingPlatform) {
          if (mounted) {
            setSiteId(existingSiteId);
            setSiteToken(existingSiteToken);
            setPlatform(existingPlatform);
            setDaysRange(sessionStorage.getItem('days_range') || '30');
            setSitesLoaded(true);
            setIsInitialized(true);
          }
          return;
        }

        const res = await fetch(`${API}/api/sites`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          console.warn('[AppWarmup] Failed to fetch sites:', res.status);
          if (mounted) {
            setSitesLoaded(true);
            setIsInitialized(true);
          }
          return;
        }

        const sites = await res.json();

        if (!mounted) return;

        if (sites && sites.length > 0) {
          const firstSite = sites[0];
          const firstSiteId = String(firstSite.id);
          const firstSiteToken = String(firstSite.token);
          const firstSitePlatform = firstSite.platform;

          sessionStorage.setItem('site_id', firstSiteId);
          sessionStorage.setItem('site_token', firstSiteToken);
          sessionStorage.setItem('site_platform', firstSitePlatform);

          setSiteId(firstSiteId);
          setSiteToken(firstSiteToken);
          setPlatform(firstSitePlatform);
          setDaysRange(sessionStorage.getItem('days_range') || '30');
          setSitesLoaded(true);
        }

        setIsInitialized(true);
        sessionStorage.setItem('app_warmup_complete', 'true');

      } catch (error) {
        console.error('[AppWarmup] Error fetching sites:', error);
        if (mounted) {
          setSitesLoaded(true);
          setIsInitialized(true);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [API, token]);

  // Fetch GA4 property id once we have sites loaded
  useEffect(() => {
    if (!token || !sitesLoaded || !siteId || siteId === 'null') return;

    let mounted = true;

    (async () => {
      try {
        const res = await fetch(`${API}/integrations/property`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const { propertyId } = await res.json();
        if (mounted) {
          setPropertyId(propertyId || null);
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      mounted = false;
    };
  }, [API, token, sitesLoaded, siteId]);

  // Preload the heavy API calls only after we have a valid siteId
  useEffect(() => {
    if (!token || !siteId || !sitesLoaded || !isInitialized) {
      console.log('[AppWarmup] Skipping preload - missing data:', {
        token: !!token,
        siteId,
        sitesLoaded,
        isInitialized
      });
      return;
    }

    console.log('[AppWarmup] Starting preload with siteId:', siteId);
    const today = new Date().toISOString().slice(0, 10);
    const qs = (o) => new URLSearchParams(o).toString();

    const jobs = [
      // ========================================
      // === CORE DASHBOARD DATA ===
      // ========================================
      {
        key: `summary:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/dashboard/summary/quick?${qs({ site_id: siteId, days: daysRange })}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `overview:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/business/overview?${qs({ site_id: siteId, days: daysRange })}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `topProducts:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/business/top-products?${qs({ site_id: siteId, days: daysRange, limit: 5 })}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // === SITES & USER DATA ===
      {
        key: `sites:${token}`,
        loader: () =>
          fetch(`${API}/api/sites`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `userProfile:${token}`,
        loader: () =>
          fetch(`${API}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // ========================================
      // === ANALYTICS INSIGHTS ===
      // FIXED: Key now matches CustomerDashboard's insightsKey
      // ========================================
      {
        // CustomerDashboard uses: `insights:${siteId}:${daysRange}:${today}`
        key: `insights:${siteId}:${daysRange}:${today}`,
        loader: () =>
          fetch(`${API}/api/analytics/insights?${qs({ site_id: siteId })}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `topCustomers:${siteId}`,
        loader: () =>
          fetch(`${API}/api/analytics/top-customers?${qs({ site_id: siteId, limit: 3 })}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          }).then(r => r.json()).catch(() => null)
      },

      // === DEMOGRAPHICS ===
      {
        key: `demographics:${siteId}:7days`,
        loader: () =>
          fetch(`${API}/api/analytics/demographics?` + qs({ site_id: siteId, days: '7' }), {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          }).then(r => r.json()).catch(() => null)
      },

      // ========================================
      // === RAGE CLICKS DATA ===
      // FIXED: Key now matches UXInsightsPage's key
      // ========================================
      {
        // UXInsightsPage uses: `rageClicksAlerts:${siteId}:${daysForAlerts}`
        key: `rageClicksAlerts:${siteId}:30`,
        loader: () =>
          fetch(`${API}/api/analytics/rage-clicks?${qs({
            site_id: siteId,
            threshold: '4',
            window_sec: '10',
            days: '30'
          })}`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          }).then(r => r.json()).catch(() => null)
      },

      // === SESSIONS ===
      {
        key: `sessions:${siteId}:${today}`,
        loader: () =>
          fetch(`${API}/sessions?site_id=${siteId}&date=${today}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },
      {
        key: `funnelRealtime:${siteId}`,
        loader: () =>
          fetch(`${API}/funnel/realtime?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // === TIMESERIES ===
      {
        key: `timeseries:${siteId}:30days`,
        loader: () =>
          fetch(`${API}/api/analytics/report/timeseries?${qs({ site_id: siteId, start_date: '30daysAgo', end_date: 'today' })}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },

      // === INTEGRATION STATUS ===
      {
        key: `integrationStatus:${token}`,
        loader: () =>
          fetch(`${API}/integrations/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => ({ connected: false }))
      },
      {
        key: `ga4Status:${token}`,
        loader: () =>
          fetch(`${API}/integrations/status/ga4`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => ({ connected: false }))
      },

      // === NOTIFICATIONS ===
      {
        key: `notifications:${token}`,
        loader: () =>
          fetch(`${API}/api/notifications`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },

      // === BUSINESS SESSIONS ===
      {
        key: `businessSessions:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/business/sessions?site_id=${siteId}&days=${daysRange}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // ========================================
      // === HEATMAP DATA ===
      // FIXED: Key now matches UXInsightsPage's key format
      // ========================================
      {
        // UXInsightsPage uses: `pages:${selectedSite}`
        key: `pages:${siteId}`,
        loader: () =>
          fetch(`${API}/pages?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },
      {
        // UXInsightsPage uses: `heatmap:${selectedSite}:${normalizedPath || 'all'}:${mode}:${timeRange}:${userType}`
        // Preload the default: all pages, clicks mode, 24h, all users
        key: `heatmap:${siteId}:all:clicks:24:all`,
        loader: () =>
          fetch(`${API}/analytics/heatmap?site_id=${siteId}&bucket_size=50&hours=24&mode=clicks&user_type=all`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // === DROPOFFS ===
      {
        key: `dropoffsSummary:${siteId}`,
        loader: () =>
          fetch(`${API}/funnel/dropoffs/summary?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },
      {
        key: `dropoffsAnalytics:${siteId}`,
        loader: () =>
          fetch(`${API}/funnel/dropoffs/analytics?site_id=${siteId}&days=7`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },

      // ========================================
      // === CUSTOMER INTELLIGENCE DATA ===
      // FIXED: Key now matches CustomerDashboard exactly
      // ========================================
      {
        // CustomerDashboard uses: `customer-intel:dashboard:${siteId}:${daysRange}` (NO v2!)
        key: `customer-intel:dashboard:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/dashboard?site_id=${siteId}&days=${daysRange}&v=2&_=${Date.now()}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `customer-intel:pairs:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/product-pairs?site_id=${siteId}&days=${daysRange}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null)
      },
      {
        key: `customer-intel:top-products:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/top-products?site_id=${siteId}&days=${daysRange}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null)
      },
      {
        key: `customer-intel:funnel:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/funnel?site_id=${siteId}&days=${daysRange}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null)
      },
      {
        key: `customer-intel:metrics:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/dashboard/summary/detailed?days=${daysRange}&site_id=${siteId}&force=1&_=${Date.now()}`, {
            headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-store' },
            cache: 'no-store',
          }).then(r => r.json()).catch(() => null)
      },
      {
        key: `customer-intel:insights:${siteId}:${daysRange}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/ai-insights?site_id=${siteId}&days=${daysRange}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => r.json()).then(data => data.summary || '').catch(() => null)
      },

      // Session details for Quick View
      {
        key: `sessionDetails:${siteId}:${today}`,
        loader: () =>
          fetch(`${API}/sessions/all-details?site_id=${siteId}&date=${today}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => [])
      },

      // Pre-generate customer export data
      {
        key: `customerExportData:${siteId}`,
        loader: () =>
          fetch(`${API}/api/customer-intelligence/export-all?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => { })
      },

      // ========================================
      // === SOCIAL INTELLIGENCE / LISTENING DATA ===
      // ========================================

      // Sentiment Summary - main overview data
      {
        key: `social-intel:summary:${siteId}`,
        loader: () =>
          fetch(`${API}/api/social-listening/sentiment/summary?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Recent Reviews (last 30 days, all sentiment)
      {
        key: `social-intel:reviews:${siteId}:all:30`,
        loader: () =>
          fetch(`${API}/api/social-listening/reviews?site_id=${siteId}&days=30&limit=50`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Reviews for "All time" (365 days) - matches CustomerVoicePage default
      {
        key: `social-intel:reviews:${siteId}:all:365`,
        loader: () =>
          fetch(`${API}/api/social-listening/reviews?site_id=${siteId}&days=365&limit=50`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Sentiment Trend (30 days)
      {
        key: `social-intel:trend:${siteId}:30`,
        loader: () =>
          fetch(`${API}/api/social-listening/sentiment/trend?site_id=${siteId}&days=30`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Sentiment Trend (365 days) - matches CustomerVoicePage "All time"
      {
        key: `social-intel:trend:${siteId}:365`,
        loader: () =>
          fetch(`${API}/api/social-listening/sentiment/trend?site_id=${siteId}&days=365`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Product Sentiments
      {
        key: `social-intel:products:${siteId}`,
        loader: () =>
          fetch(`${API}/api/social-listening/sentiment/products?site_id=${siteId}&limit=30`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Social Sentiment Prescriptions
      {
        key: `prescriptions:social:${siteId}`,
        loader: () =>
          fetch(`${API}/api/prescriptions?site_id=${siteId}&category=social_sentiment&limit=10`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : []).catch(() => [])
      },

      // Negative reviews specifically (for quick attention)
      {
        key: `social-intel:reviews:${siteId}:negative:30`,
        loader: () =>
          fetch(`${API}/api/social-listening/reviews?site_id=${siteId}&days=30&sentiment=negative&limit=20`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },
      // Add these to your jobs array in AppWarmup.jsx:

      // Gemini Social Analysis (main social listening data)
      {
        key: `gemini-analysis:${siteId}`,
        loader: () =>
          fetch(`${API}/api/social-listening/gemini/analysis?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Gemini Social Mentions (individual mentions)
      {
        key: `gemini-mentions:${siteId}`,
        loader: () =>
          fetch(`${API}/api/social-listening/gemini/mentions?site_id=${siteId}&limit=20`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : []).catch(() => [])
      },
      // Site Status (for inactive site banner)
      {
        key: `site-status:${siteId}`,
        loader: () =>
          fetch(`${API}/api/sites/${siteId}/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // ========================================
      // === PRESCRIPTIONS DATA ===
      // ========================================

      // All active prescriptions
      {
        key: `prescriptions:all:${siteId}`,
        loader: () =>
          fetch(`${API}/api/prescriptions?site_id=${siteId}&status=active&limit=50`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : []).catch(() => [])
      },

      // Prescription counts
      {
        key: `prescriptions:count:${siteId}`,
        loader: () =>
          fetch(`${API}/api/prescriptions/count?site_id=${siteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null)
      },

      // Critical prescriptions
      {
        key: `prescriptions:critical:${siteId}`,
        loader: () =>
          fetch(`${API}/api/prescriptions?site_id=${siteId}&urgency=critical&limit=10`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : []).catch(() => [])
      },
    ];

    const GA4_DEFAULT_DAYS = 30;

    // === GA4 SPECIFIC DATA (if propertyId exists) ===
    if (propertyId && siteId) {
      jobs.push(
        {
          key: `pageViews:${GA4_DEFAULT_DAYS}`,
          loader: () =>
            fetch(`${API}/api/business/page-views?limit=5&start_date=${GA4_DEFAULT_DAYS}daysAgo&end_date=yesterday`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).catch(() => null)
        },
        {
          key: `ga4Perf:${GA4_DEFAULT_DAYS}`,
          loader: () =>
            fetch(`${API}/api/business/performance?days=${GA4_DEFAULT_DAYS}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).catch(() => null)
        },
        {
          key: `uniqueVisitors:${GA4_DEFAULT_DAYS}`,
          loader: () =>
            fetch(`${API}/api/business/unique-visitors?days=${GA4_DEFAULT_DAYS}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).catch(() => null)
        },
        {
          key: `ga4Report:${propertyId}:30days`,
          loader: () =>
            fetch(`${API}/integrations/report?start_date=30daysAgo&end_date=today&limit=100`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).catch(() => null)
        },
        {
          key: `ga4Batch:${propertyId}:${GA4_DEFAULT_DAYS}`,
          loader: () =>
            fetch(`${API}/api/business/analytics/batch?site_id=${encodeURIComponent(siteId)}&days=${GA4_DEFAULT_DAYS}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).catch(() => null)
        }
      );
    }

    // === PLATFORM-SPECIFIC SALES DATA ===
    if (siteId && siteToken && platform) {
      jobs.push({
        key: `salesData:${siteId}:${platform}:7days`,
        loader: () => {
          let salesPath;
          switch (platform.toLowerCase()) {
            case 'bigcommerce':
              const storeHash = sessionStorage.getItem('bigcommerce_store_hash');
              if (!storeHash) return Promise.resolve(null);
              salesPath = `/analytics/bigcommerce/orders?store_hash=${encodeURIComponent(storeHash)}&token=${siteToken}&days=7`;
              break;
            case 'shopify':
              salesPath = `/analytics/shopify/orders?site_id=${siteId}&site_token=${siteToken}&days=7`;
              break;
            case 'woocommerce':
              const secret = sessionStorage.getItem('site_consumer_secret');
              const siteUrl = sessionStorage.getItem('site_url');
              if (!secret || !siteUrl) return Promise.resolve(null);
              salesPath = `/analytics/woocommerce/orders?store_url=${encodeURIComponent(siteUrl)}&consumer_key=${siteToken}&consumer_secret=${secret}&days=7`;
              break;
            default:
              const defaultUrl = sessionStorage.getItem('site_url');
              if (!defaultUrl) return Promise.resolve(null);
              salesPath = `/analytics/custom/orders?domain=${encodeURIComponent(defaultUrl)}&days=7`;
              break;
          }

          return fetch(`${API}${salesPath}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()).catch(() => null);
        }
      });
    }

    // Use setTimeout to defer preloading slightly, allowing Dashboard to initialize first
    const timeoutId = setTimeout(() => {
      console.log(`[AppWarmup] Preloading ${jobs.length} endpoints for site ${siteId}`);
      cache.preload(jobs); // idempotent; safe to mount once
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [API, token, siteId, daysRange, propertyId, cache, sitesLoaded, isInitialized, platform, siteToken]);

  return null; // renders nothing
}